/**
 * RecordProcedureFormModal — Ustawienia → „Procedury SQL" → zakładka „Kartoteki", modal create/edit
 * (mirror `processes/ProcessProcedureFormModal.tsx` — edytor SQL + edytor parametrów + sekcja
 * „Testuj"; RadioGroup `kind` procesów ZASTĄPIONY dwoma dropdownami: typ kartoteki + moment
 * uruchomienia: kontrola przed zapisem, po zapisie, kontrola przed usunięciem albo „Na przycisk").
 *
 * `kind` NIE JEST polem formularza — backend wyprowadza je serwerowo z `lifecycleEvent`
 * (control/delete → validation READ ONLY, saved/button → action zapis, `recordProcedureKind()` w
 * `apps/api/lib/validators/record-procedures.ts`). Front nigdy nie wysyła `kind`.
 *
 * `recordType` i `lifecycleEvent` (razem: „slot") są NIEEDYTOWALNE po utworzeniu — backend
 * w ogóle nie przyjmuje tych pól w `UpdateRecordProcedureSchema` (cicho ignorowane), więc UI
 * zamraża oba dropdowny w `mode==='edit'` (analogicznie do `kind` w edytorze Procesów).
 *
 * Zdarzenie „Na przycisk" (`button`): dodatkowe pole „Limit czasu (min)" (liczba całkowita
 * 1–60, domyślnie 1; do 1 min zadanie trafia do kolejki szybkiej, dłuższe do wolnej) — widoczne
 * tylko dla tego zdarzenia i edytowalne także w trybie edycji. Przy każdym innym zdarzeniu
 * formularz oddaje `timeoutMinutes: null` (serwer odrzuca to pole poza przyciskiem). Kartoteki
 * z samym przyciskiem (kontakty kontrahentów, pracownicy, użytkownicy, towary — patrz
 * `isButtonOnlyRecordType`) dostają to zdarzenie automatycznie po wyborze typu, a dropdown
 * zdarzenia jest wtedy zablokowany.
 *
 * Procedura „Na przycisk" nie ma własnych parametrów — przy kliknięciu nikt nie poda ich wartości,
 * więc zapytanie dostaje wyłącznie parametry systemowe. Dla przycisku formularz chowa edytor
 * „Parametry zapytania" i wartości próbne w „Testuj", zawsze wysyła `declaredParams: []` (zapis,
 * edycja, „Testuj"), a przełączenie z innego zdarzenia czyści wcześniej dodane parametry (z
 * krótką informacją inline, bez okna potwierdzenia).
 *
 * Sekcja „Testuj" woła `POST /api/settings/record-procedures/test-sql` (dry-run — dla
 * `lifecycleEvent='saved'` i `'button'` egzekutor ZAWSZE robi ROLLBACK, żadna zmiana nie jest
 * utrwalana; test przycisku ma na serwerze limit 25 s). Przycisk dostaje tylko dane systemowe
 * (`:fields`/`:additional` są NULL), więc dla niego panel nie pokazuje pól JSON, nie wysyła ich
 * w `procContext`, a edytor SQL nie podpowiada tych dwóch parametrów.
 * Endpoint wymaga kontekstu rezerwowego silnika (`procContext`: recordType z pickera +
 * userId/companyId jako UUID + fields/additional jako text-JSON) — poza formularzem
 * create/edit nie ma żadnego realnego rekordu, więc `userId`/`companyId` są domyślnie
 * wypełnione PLACEHOLDEREM (nil UUID, userId = bieżący zalogowany user) i chowane pod
 * zwijaną sekcję „Kontekst zaawansowany".
 *
 * Procedura „Na przycisk" na DOKUMENCIE może zależeć od klasy dokumentu (`:record_class`) — przy
 * prawdziwym uruchomieniu serwer podstawia klasę strony, na której stoi przycisk. Dlatego w sekcji
 * „Testuj" dla typu „dokument" z momentem „Na przycisk" jest wybór klasy (7 klas; domyślnie faktura
 * zakupu), wysyłany jako `procContext.recordClass`. Dla pozostałych typów i momentów pola nie ma i nie
 * jest wysyłane.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentClass } from '@saas/shared';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { SqlField } from '@/components/ui/SqlField';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import {
  BUTTON_TIMEOUT_MINUTES_DEFAULT,
  BUTTON_TIMEOUT_MINUTES_MAX,
  BUTTON_TIMEOUT_MINUTES_MIN,
  isButtonOnlyRecordType,
  LIFECYCLE_EVENT_OPTIONS,
  RECORD_PROCEDURE_PARAM_TYPES,
  RECORD_TYPE_OPTIONS,
  type RecordLifecycleEvent,
  type RecordProcedureParam,
  type RecordProcedureParamType,
  type RecordProcedureTestContext,
  useTestRecordProcedureSql,
} from '@/lib/record-procedures/api';
import { testSqlErrorKey } from '@/lib/record-procedures/error-keys';
import {
  buildSqlParamCompletions,
  RECORD_BUTTON_PROCEDURE_RESERVED_PARAMS,
  RECORD_PROCEDURE_RESERVED_PARAMS,
} from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';
import { documentClassLabelKey } from '@/lib/system-entity-adapters';
import { cn } from '@/lib/utils';

export interface RecordProcedureFormValues {
  name: string;
  description: string;
  recordType: string;
  lifecycleEvent: RecordLifecycleEvent;
  sql: string;
  declaredParams: RecordProcedureParam[];
  /** Limit czasu w minutach — liczba tylko dla `button`, dla pozostałych zdarzeń `null`. */
  timeoutMinutes: number | null;
}

export function emptyRecordProcedureFormValues(): RecordProcedureFormValues {
  return {
    name: '',
    description: '',
    recordType: RECORD_TYPE_OPTIONS[0]?.value ?? '',
    lifecycleEvent: 'control',
    sql: '',
    declaredParams: [],
    timeoutMinutes: null,
  };
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: RecordProcedureFormValues;
  saving?: boolean;
  /** Błędy pól zmapowane z odpowiedzi 409 backendu (np. NAME_TAKEN/SLOT_TAKEN → pole `name`). */
  serverErrors?: Record<string, string>;
  onSubmit: (values: RecordProcedureFormValues) => void;
  onClose: () => void;
}

/** Placeholder — dry-run nie odwołuje się do realnego rekordu, więc nil UUID jest bezpieczny. */
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** 7 klas dokumentów w kolejności listy (jak selektor klasy w wyszukiwarce rekordów). */
const DOCUMENT_CLASS_VALUES: readonly DocumentClass[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
  'specjalne',
];

/** Klasa wybrana w teście przycisku na dokumencie, dopóki admin nie wybierze innej. */
const DEFAULT_TEST_DOCUMENT_CLASS: DocumentClass = 'purchase_invoice';

function isDocumentClass(value: string): value is DocumentClass {
  return DOCUMENT_CLASS_VALUES.some((cls) => cls === value);
}

/** Klucz tłumaczenia etykiety klasy — istniejące tłumaczenia (te same co w selektorze klasy dokumentów). */
function documentClassLabelKeyOf(cls: DocumentClass): string {
  const key = documentClassLabelKey(cls);
  if (key === null) {
    throw new Error(`RecordProcedureFormModal: brak etykiety dla klasy dokumentu ${cls}`);
  }
  return key;
}

/** Mirror walidatora backendu (`paramNameSchema`, `lib/validators/record-procedures.ts`). */
const PARAM_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Pole limitu trzymamy jako tekst (jak pole liczbowe w `DictionaryItemFormModal`), żeby dało się je wyczyścić. */
function timeoutToStr(timeoutMinutes: number | null): string {
  return String(timeoutMinutes ?? BUTTON_TIMEOUT_MINUTES_DEFAULT);
}

/** Całkowita liczba minut z pola limitu albo `null`, gdy tekst nie jest liczbą całkowitą w 1–60. */
function parseTimeoutMinutes(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const minutes = Number(trimmed);
  return minutes >= BUTTON_TIMEOUT_MINUTES_MIN && minutes <= BUTTON_TIMEOUT_MINUTES_MAX
    ? minutes
    : null;
}

/**
 * Procedura „Na przycisk" nie ma własnych parametrów (przy kliknięciu nikt nie poda ich wartości —
 * dostaje wyłącznie parametry systemowe), więc jej lista zadeklarowanych parametrów jest zawsze
 * pusta; dla pozostałych zdarzeń — wartości z procedury.
 */
function paramsForEvent(
  lifecycleEvent: RecordLifecycleEvent,
  declaredParams: RecordProcedureParam[],
): RecordProcedureParam[] {
  return lifecycleEvent === 'button' ? [] : declaredParams;
}

function initialParams(values: RecordProcedureFormValues): RecordProcedureParam[] {
  return paramsForEvent(values.lifecycleEvent, values.declaredParams);
}

interface SnapshotInput {
  name: string;
  description: string;
  recordType: string;
  lifecycleEvent: RecordLifecycleEvent;
  sql: string;
  declaredParams: RecordProcedureParam[];
  timeoutStr: string;
}

/**
 * Migawka pól procedury do jawnego `dirty` (pytanie przy zamknięciu tłem/Esc): typ kartoteki,
 * moment uruchomienia i typ parametru wybiera się z listy (bez zdarzeń wpisywania). Limit czasu
 * liczy się tylko dla „Na przycisk" (przy innych zdarzeniach pole jest ukryte), a lista
 * parametrów — tylko dla zdarzeń innych niż „Na przycisk". Pola sekcji „Testuj" (wartości
 * próbne, kontekst) NIE są częścią procedury i się nie liczą.
 */
function formSnapshot(values: SnapshotInput): string {
  return JSON.stringify([
    values.name,
    values.description,
    values.recordType,
    values.lifecycleEvent,
    values.sql,
    paramsForEvent(values.lifecycleEvent, values.declaredParams).map((p) => [
      p.name,
      p.type,
      p.required,
    ]),
    values.lifecycleEvent === 'button' ? values.timeoutStr : null,
  ]);
}

function initialSnapshot(values: RecordProcedureFormValues): string {
  return formSnapshot({ ...values, timeoutStr: timeoutToStr(values.timeoutMinutes) });
}

export function RecordProcedureFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [recordType, setRecordType] = useState(initialValues.recordType);
  const [lifecycleEvent, setLifecycleEvent] = useState<RecordLifecycleEvent>(
    initialValues.lifecycleEvent,
  );
  const [sql, setSql] = useState(initialValues.sql);
  const [declaredParams, setDeclaredParams] = useState<RecordProcedureParam[]>(() =>
    initialParams(initialValues),
  );
  // Informacja inline po wyczyszczeniu listy parametrów przy przełączeniu na „Na przycisk".
  const [paramsClearedNotice, setParamsClearedNotice] = useState(false);
  const [timeoutStr, setTimeoutStr] = useState(() => timeoutToStr(initialValues.timeoutMinutes));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Migawka wartości z chwili otwarcia (rodzic może przeliczyć `initialValues` przy otwartym oknie).
  const [baseline, setBaseline] = useState<string>(() => initialSnapshot(initialValues));

  // Sekcja „Testuj" — przykładowe wartości zadeklarowanych parametrów + kontekst rezerwowy
  // silnika (domyślnie placeholder, opcjonalnie nadpisywalny w sekcji zaawansowanej).
  const [testParamValues, setTestParamValues] = useState<Record<string, string>>({});
  const [testFieldsError, setTestFieldsError] = useState<string | null>(null);
  const [showAdvancedContext, setShowAdvancedContext] = useState(false);
  const [contextRecordId, setContextRecordId] = useState(NIL_UUID);
  const [contextUserId, setContextUserId] = useState(me?.user.id ?? NIL_UUID);
  const [contextCompanyId, setContextCompanyId] = useState('');
  const [contextFields, setContextFields] = useState('');
  const [contextAdditional, setContextAdditional] = useState('');
  // Klasa dokumentu w teście przycisku na dokumencie (pole widoczne tylko dla `document` + „Na przycisk”).
  const [testRecordClass, setTestRecordClass] = useState<DocumentClass>(
    DEFAULT_TEST_DOCUMENT_CLASS,
  );

  const testMutation = useTestRecordProcedureSql();
  const sqlSchema = useTenantSqlSchema(open);
  const sqlDiagnostics = useSqlTestDiagnostics(sql, testMutation);
  // Przycisk dostaje tylko dane systemowe (`:fields`/`:additional` są NULL) — nie podpowiadamy ich.
  const sqlParams = useMemo(
    () =>
      buildSqlParamCompletions(
        t,
        lifecycleEvent === 'button'
          ? RECORD_BUTTON_PROCEDURE_RESERVED_PARAMS
          : RECORD_PROCEDURE_RESERVED_PARAMS,
        paramsForEvent(lifecycleEvent, declaredParams),
      ),
    [t, lifecycleEvent, declaredParams],
  );

  useEffect(() => {
    if (open) {
      setName(initialValues.name);
      setDescription(initialValues.description);
      setRecordType(initialValues.recordType);
      setLifecycleEvent(initialValues.lifecycleEvent);
      setSql(initialValues.sql);
      setDeclaredParams(initialParams(initialValues));
      setParamsClearedNotice(false);
      setTimeoutStr(timeoutToStr(initialValues.timeoutMinutes));
      setBaseline(initialSnapshot(initialValues));
      setTestParamValues({});
      setShowAdvancedContext(false);
      setContextRecordId(NIL_UUID);
      setContextUserId(me?.user.id ?? NIL_UUID);
      setContextCompanyId('');
      setContextFields('');
      setContextAdditional('');
      setTestRecordClass(DEFAULT_TEST_DOCUMENT_CLASS);
      testMutation.reset();
      setTestFieldsError(null);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  if (!open) return null;

  const isDirty =
    formSnapshot({
      name,
      description,
      recordType,
      lifecycleEvent,
      sql,
      declaredParams,
      timeoutStr,
    }) !== baseline;

  // Slot (typ kartoteki + moment uruchomienia) immutable po utworzeniu — patrz docstring pliku.
  const isSlotLocked = mode === 'edit';
  const slotLockedReason = t('recordProcedures.form.slotImmutableHint');
  // Kartoteki z samym przyciskiem: jedyne dostępne zdarzenie to „Na przycisk" (ustawiane po
  // wyborze typu), więc dropdown zdarzenia jest zablokowany z wyjaśnieniem.
  const isButtonOnlyType = isButtonOnlyRecordType(recordType);
  const lifecycleHint = isSlotLocked
    ? slotLockedReason
    : isButtonOnlyType
      ? t('recordProcedures.form.buttonOnlyHint')
      : undefined;

  // Przykład dopasowany do momentu uruchomienia (control/delete = walidacja SELECT, saved =
  // akcja modyfikująca dane, button = akcja zwracająca komunikat w kolumnie `message`) ORAZ do
  // typu kartoteki (kontrahent ma nip, dokument ma title — wspólny przykład byłby myląco nie na
  // temat dla drugiego typu; pozostałe kartoteki dostają przykład bez nazw ich tabel).
  const isButton = lifecycleEvent === 'button';
  // Lista parametrów, którą formularz faktycznie wysyła (zapis i „Testuj"): dla przycisku zawsze pusta.
  const requestParams = paramsForEvent(lifecycleEvent, declaredParams);
  const isAction = lifecycleEvent === 'saved' || isButton;
  const isDocument = recordType === 'document';
  const isContractor = recordType === 'contractor';
  const sqlExample = isButton
    ? isDocument
      ? t('recordProcedures.form.sqlExampleButtonDocument')
      : isContractor
        ? t('recordProcedures.form.sqlExampleButtonContractor')
        : t('recordProcedures.form.sqlExampleButtonGeneric')
    : isDocument
      ? isAction
        ? t('recordProcedures.form.sqlExampleDocumentAction')
        : t('recordProcedures.form.sqlExampleDocument')
      : isAction
        ? t('recordProcedures.form.sqlExampleAction')
        : t('recordProcedures.form.sqlExample');
  const sqlHint = isButton
    ? t('recordProcedures.form.sqlHintButton')
    : isAction
      ? t('recordProcedures.form.sqlHintAction')
      : t('recordProcedures.form.sqlHintValidation');

  function handleRecordTypeChange(value: string) {
    setRecordType(value);
    // Wynik „Testuj" dotyczył poprzedniej kartoteki — po zmianie typu znika.
    testMutation.reset();
    if (isButtonOnlyRecordType(value)) {
      handleLifecycleEventChange('button');
    }
  }

  function handleTestRecordClassChange(value: string) {
    if (!isDocumentClass(value)) {
      throw new Error(`RecordProcedureFormModal: nieznana klasa dokumentu „${value}”`);
    }
    setTestRecordClass(value);
    // Wynik „Testuj" dotyczył poprzedniej klasy — po zmianie znika.
    testMutation.reset();
  }

  function handleLifecycleEventChange(value: RecordLifecycleEvent) {
    setLifecycleEvent(value);
    // Wynik „Testuj" dotyczył poprzedniego momentu uruchomienia — po zmianie znika.
    testMutation.reset();
    // Ewentualny błąd limitu czasu (i listy parametrów) dotyczył poprzedniego zdarzenia.
    setErrors((prev) => ({ ...prev, timeoutMinutes: '', declaredParams: '' }));
    if (value === 'button') {
      // Pola JSON `:fields`/`:additional` znikają przy przycisku — ich błąd też.
      setTestFieldsError(null);
      // Przycisk nie ma własnych parametrów (nikt nie poda ich wartości przy kliknięciu) —
      // lista zadeklarowanych parametrów jest czyszczona, z krótką informacją inline.
      if (declaredParams.length > 0) {
        setDeclaredParams([]);
        setTestParamValues({});
        setParamsClearedNotice(true);
      }
    } else {
      setParamsClearedNotice(false);
    }
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) {
      next.name = t('recordProcedures.form.errors.nameRequired');
    } else if (name.trim().length > 200) {
      next.name = t('recordProcedures.form.errors.nameLength');
    }
    if (!sql.trim()) {
      next.sql = t('recordProcedures.form.errors.sqlRequired');
    }
    const names = requestParams.map((p) => p.name.trim());
    if (names.some((n) => !PARAM_NAME_REGEX.test(n))) {
      next.declaredParams = t('recordProcedures.form.errors.sqlParamNameInvalid');
    } else if (new Set(names).size !== names.length) {
      next.declaredParams = t('recordProcedures.form.errors.sqlParamNameDuplicate');
    }
    if (isButton && parseTimeoutMinutes(timeoutStr) === null) {
      next.timeoutMinutes = t('recordProcedures.form.errors.timeoutRange', {
        min: BUTTON_TIMEOUT_MINUTES_MIN,
        max: BUTTON_TIMEOUT_MINUTES_MAX,
      });
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      recordType,
      lifecycleEvent,
      sql: sql.trim(),
      declaredParams: requestParams.map((p) => ({ ...p, name: p.name.trim() })),
      // Limit wyłącznie dla przycisku (po walidacji wyżej parsowanie nie zwróci null).
      timeoutMinutes: isButton ? parseTimeoutMinutes(timeoutStr) : null,
    });
  }

  function updateParam(index: number, patch: Partial<RecordProcedureParam>) {
    setDeclaredParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeParam(index: number) {
    setDeclaredParams((prev) => prev.filter((_, i) => i !== index));
  }

  function handleTest() {
    if (!sql.trim()) return;
    setTestFieldsError(null);
    const paramValues: Record<string, string> = {};
    for (const param of requestParams) {
      const raw = testParamValues[param.name];
      if (raw !== undefined && raw !== '') paramValues[param.name] = raw;
    }

    // Zadeklarowane parametry NIGDY nie dostają wartości przy prawdziwym zapisie/usunięciu/
    // po-zapisie — silnik zawsze woła z pustym paramValues (record-control.ts, record-saved.ts).
    // Jedyny kanał danych rekordu to :fields. Żeby test to poprawnie odzwierciedlał i żeby dało
    // się wpisać wartość pola bez ręcznego JSON-a, dopisujemy wpisane tu wartości do testowych
    // :fields pod tą samą nazwą (nadpisują ewentualny klucz z ręcznie wpisanego JSON-a w
    // „Kontekst zaawansowany" — deklarowany parametr jest bardziej precyzyjną intencją).
    // Walidacja JSON-a leci NIEZALEŻNIE od tego czy jest jakiś zadeklarowany parametr — inaczej
    // niepoprawny/nie-obiektowy JSON w samym polu „Pola kartoteki" przechodziłby bez ostrzeżenia.
    //
    // WYJĄTEK — „Na przycisk": zapytanie dostaje tylko dane systemowe (id rekordu, typ, klasa,
    // użytkownik, firma), a `:fields` i `:additional` są NULL — tak jak przy prawdziwym
    // uruchomieniu. Pól JSON nie pokazujemy, nie walidujemy i w ogóle nie wysyłamy (serwer
    // przy braku pola podstawia NULL), a wartości zadeklarowanych parametrów NIE trafiają do
    // `:fields`.
    const recordState: Pick<RecordProcedureTestContext, 'fields' | 'additional'> = {};
    if (!isButton) {
      const rawFields = contextFields.trim();
      let base: Record<string, unknown> = {};
      if (rawFields) {
        try {
          const parsed: unknown = JSON.parse(rawFields);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('record-procedure test: :fields musi być obiektem JSON');
          }
          base = parsed as Record<string, unknown>;
        } catch {
          setTestFieldsError(t('recordProcedures.form.errors.testFieldsInvalidJson'));
          testMutation.reset();
          return;
        }
      }
      recordState.fields =
        rawFields || Object.keys(paramValues).length > 0
          ? JSON.stringify({ ...base, ...paramValues })
          : null;
      recordState.additional = contextAdditional.trim() ? contextAdditional.trim() : null;
    }

    testMutation.mutate({
      sql: sql.trim(),
      lifecycleEvent,
      declaredParams: requestParams.map((p) => ({ ...p, name: p.name.trim() })),
      ...(Object.keys(paramValues).length > 0 ? { paramValues } : {}),
      procContext: {
        recordId: contextRecordId.trim() ? contextRecordId.trim() : null,
        recordType,
        userId: contextUserId.trim(),
        companyId: contextCompanyId.trim() ? contextCompanyId.trim() : null,
        // Klasa dokumentu — tylko dla przycisku na dokumencie; inne typy i momenty jej nie dostają.
        ...(isDocument && isButton ? { recordClass: testRecordClass } : {}),
        ...recordState,
      },
    });
  }

  const testError = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const data =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? (err.response.data as {
                error?: string;
                message?: string;
                paramName?: string;
              })
            : null;
        const code = data?.error;
        const translated = t(testSqlErrorKey(code), { paramName: data?.paramName ?? '' });
        // EXECUTION_FAILED / RECORD_PROCEDURE_RESULT_SHAPE niosą realny komunikat serwera (błąd
        // PG / szczegół złego kształtu wyniku) — dokładamy go pod tłumaczonym nagłówkiem: to
        // diagnostyka dla autora SQL (endpoint gate'owany permission autoringu), nie przeciek.
        if (
          (code === 'EXECUTION_FAILED' || code === 'RECORD_PROCEDURE_RESULT_SHAPE') &&
          data?.message
        ) {
          return `${translated} ${data.message}`;
        }
        return translated;
      })()
    : null;
  const testResult = testMutation.data ?? null;

  const titleKey =
    mode === 'create' ? 'recordProcedures.form.titleCreate' : 'recordProcedures.form.titleEdit';

  const severityBannerClass: Record<'ok' | 'warning' | 'error', string> = {
    ok: 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/40 dark:text-green-300',
    warning:
      'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    error:
      'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300',
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(titleKey)}
      // Szerokie okno — dane procedury w dwóch kolumnach, pod nimi pole SQL na całą szerokość,
      // na dole parametry obok „Testuj” (bez przewijania).
      size="2xl"
      testId="record-procedure-form-modal"
      guardUnsaved
      dirty={isDirty}
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="record-procedure-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || sql.trim().length === 0}
            loading={saving}
            data-testid="record-procedure-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div
        className="grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2"
        data-testid="record-procedure-form-layout"
      >
        <div className="space-y-3">
          <FormField label={t('recordProcedures.form.name')} error={errors.name}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder={t('recordProcedures.form.namePlaceholder')}
              data-testid="record-procedure-form-name"
            />
          </FormField>
          <FormField label={t('recordProcedures.form.description')}>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('recordProcedures.form.descriptionPlaceholder')}
              rows={2}
              data-testid="record-procedure-form-description"
            />
          </FormField>
        </div>

        <div className="space-y-3">
          <FormField
            label={t('recordProcedures.form.recordType')}
            hint={isSlotLocked ? slotLockedReason : undefined}
          >
            <Dropdown
              value={recordType}
              onChange={handleRecordTypeChange}
              disabled={isSlotLocked}
              options={RECORD_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              }))}
              data-testid="record-procedure-form-record-type"
            />
          </FormField>
          <FormField label={t('recordProcedures.form.lifecycleEvent')} hint={lifecycleHint}>
            <Dropdown
              value={lifecycleEvent}
              onChange={(value) => handleLifecycleEventChange(value as RecordLifecycleEvent)}
              disabled={isSlotLocked || isButtonOnlyType}
              options={LIFECYCLE_EVENT_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              }))}
              data-testid="record-procedure-form-lifecycle-event"
            />
          </FormField>
          {isButton && (
            <FormField
              label={t('recordProcedures.form.timeoutMinutes')}
              hint={t('recordProcedures.form.timeoutHint', {
                max: BUTTON_TIMEOUT_MINUTES_MAX,
              })}
              error={errors.timeoutMinutes}
            >
              <Input
                type="number"
                min={BUTTON_TIMEOUT_MINUTES_MIN}
                max={BUTTON_TIMEOUT_MINUTES_MAX}
                step={1}
                value={timeoutStr}
                onChange={(e) => {
                  setTimeoutStr(e.target.value);
                  setErrors((prev) => ({ ...prev, timeoutMinutes: '' }));
                }}
                data-testid="record-procedure-form-timeout"
              />
            </FormField>
          )}
          {isButton && paramsClearedNotice && (
            <p
              role="status"
              className={`rounded-md border px-3 py-2 text-sm ${severityBannerClass.warning}`}
              data-testid="record-procedure-form-params-cleared-notice"
            >
              {t('recordProcedures.form.paramsClearedNotice')}
            </p>
          )}
        </div>

        {/* Pole SQL na całą szerokość okna (obie kolumny). */}
        <div className="space-y-1 lg:col-span-2">
          <div className="flex justify-end">
            <InsertExampleButton
              currentValue={sql}
              example={sqlExample}
              onInsert={(value) => {
                setSql(value);
                setErrors((prev) => ({ ...prev, sql: '' }));
              }}
              data-testid="record-procedure-form-sql-example"
            />
          </div>
          <FormField label={t('recordProcedures.form.sqlLabel')} hint={sqlHint} error={errors.sql}>
            <SqlField
              value={sql}
              onChange={(value) => {
                setSql(value);
                setErrors((prev) => ({ ...prev, sql: '' }));
              }}
              schema={sqlSchema}
              parameters={sqlParams}
              diagnostics={sqlDiagnostics}
              placeholder={t('common.examplePlaceholder', { example: sqlExample })}
              aria-label={t('recordProcedures.form.sqlLabel')}
              minHeight="200px"
              data-testid="record-procedure-form-sql"
            />
          </FormField>
        </div>

        {/* „Na przycisk" nie ma własnych parametrów (dostaje tylko parametry systemowe) — sekcji nie pokazujemy. */}
        {!isButton && (
          <FormField
            label={t('recordProcedures.form.sqlParamsTitle')}
            hint={t('recordProcedures.form.sqlParamsHint')}
            error={errors.declaredParams}
          >
            <div className="space-y-2" data-testid="record-procedure-form-params">
              {declaredParams.map((param, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={param.name}
                    onChange={(e) => {
                      updateParam(index, { name: e.target.value });
                      setErrors((prev) => ({ ...prev, declaredParams: '' }));
                    }}
                    placeholder={t('recordProcedures.form.sqlParamNamePlaceholder')}
                    containerClassName="min-w-0 flex-1"
                    data-testid={`record-procedure-form-param-name-${index}`}
                  />
                  <Dropdown
                    value={param.type}
                    onChange={(value) =>
                      updateParam(index, { type: value as RecordProcedureParamType })
                    }
                    className="w-32 shrink-0"
                    options={RECORD_PROCEDURE_PARAM_TYPES.map((type) => ({
                      value: type,
                      label: t(`admin.globalDictionaries.form.paramType.${type}`),
                    }))}
                    data-testid={`record-procedure-form-param-type-${index}`}
                  />
                  <Checkbox
                    checked={param.required}
                    onChange={(e) => updateParam(index, { required: e.target.checked })}
                    label={t('recordProcedures.form.paramRequired')}
                    containerClassName="shrink-0"
                    data-testid={`record-procedure-form-param-required-${index}`}
                  />
                  <IconButton
                    variant="danger"
                    size="sm"
                    onClick={() => removeParam(index)}
                    aria-label={t('recordProcedures.form.sqlParamRemove')}
                    data-testid={`record-procedure-form-param-remove-${index}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() =>
                  setDeclaredParams((prev) => [
                    ...prev,
                    { name: '', type: 'text', required: false },
                  ])
                }
                data-testid="record-procedure-form-param-add"
              >
                {t('recordProcedures.form.sqlParamAdd')}
              </Button>
            </div>
          </FormField>
        )}

        {/* Sekcja „Testuj" — dry-run na bazie tenanta (patrz docstring pliku). Bez sekcji
            parametrów (przycisk) zajmuje całą szerokość okna. */}
        <div
          className={cn(
            'space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3',
            isButton && 'lg:col-span-2',
          )}
          data-testid="record-procedure-form-test-section"
        >
          <p className="text-sm font-medium text-text-primary">
            {t('recordProcedures.form.testTitle')}
          </p>
          <p className="text-xs text-text-tertiary">{t('recordProcedures.form.testHint')}</p>
          {isButton && (
            <p
              className="text-xs text-text-tertiary"
              data-testid="record-procedure-form-test-button-limit"
            >
              {t('recordProcedures.form.testButtonLimitHint')}
            </p>
          )}

          {/* Wartości próbne parametrów — tylko dla zdarzeń z własnymi parametrami (nie „Na przycisk"). */}
          {requestParams.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-text-muted">
                {t('recordProcedures.form.testParamValuesHint')}
              </p>
              {requestParams
                .filter((param) => param.name.trim())
                .map((param) => (
                  <FormField key={param.name} label={param.name}>
                    <Input
                      value={testParamValues[param.name] ?? ''}
                      onChange={(e) =>
                        setTestParamValues((prev) => ({ ...prev, [param.name]: e.target.value }))
                      }
                      data-testid={`record-procedure-form-test-param-${param.name}`}
                    />
                  </FormField>
                ))}
            </div>
          )}

          {/* Procedura przycisku na dokumencie może zależeć od klasy dokumentu (`:record_class`). */}
          {isDocument && isButton && (
            <FormField
              label={t('recordProcedures.form.testRecordClass')}
              hint={t('recordProcedures.form.testRecordClassHint')}
            >
              <Dropdown
                value={testRecordClass}
                onChange={handleTestRecordClassChange}
                options={DOCUMENT_CLASS_VALUES.map((cls) => ({
                  value: cls,
                  label: t(documentClassLabelKeyOf(cls)),
                }))}
                data-testid="record-procedure-form-test-record-class"
              />
            </FormField>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvancedContext((v) => !v)}
            leftIcon={
              showAdvancedContext ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )
            }
            data-testid="record-procedure-form-advanced-toggle"
          >
            {t('recordProcedures.form.advancedContext')}
          </Button>

          {showAdvancedContext && (
            <div
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
              data-testid="record-procedure-form-advanced-context"
            >
              <p className="col-span-full text-xs text-text-tertiary">
                {t('recordProcedures.form.advancedContextHint')}
              </p>
              <FormField label={t('recordProcedures.form.contextRecordId')}>
                <Input
                  value={contextRecordId}
                  onChange={(e) => setContextRecordId(e.target.value)}
                  data-testid="record-procedure-form-context-record-id"
                />
              </FormField>
              <FormField label={t('recordProcedures.form.contextUserId')}>
                <Input
                  value={contextUserId}
                  onChange={(e) => setContextUserId(e.target.value)}
                  data-testid="record-procedure-form-context-user-id"
                />
              </FormField>
              <FormField label={t('recordProcedures.form.contextCompanyId')}>
                <Input
                  value={contextCompanyId}
                  onChange={(e) => setContextCompanyId(e.target.value)}
                  data-testid="record-procedure-form-context-company-id"
                />
              </FormField>
              {/* „Na przycisk" nie ma `:fields` ani `:additional` (NULL) — pól JSON nie pokazujemy. */}
              {!isButton && (
                <>
                  <FormField label={t('recordProcedures.form.contextFields')}>
                    <Input
                      value={contextFields}
                      onChange={(e) => setContextFields(e.target.value)}
                      data-testid="record-procedure-form-context-fields"
                    />
                  </FormField>
                  <FormField label={t('recordProcedures.form.contextAdditional')}>
                    <Input
                      value={contextAdditional}
                      onChange={(e) => setContextAdditional(e.target.value)}
                      data-testid="record-procedure-form-context-additional"
                    />
                  </FormField>
                </>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={handleTest}
            loading={testMutation.isPending}
            disabled={testMutation.isPending || sql.trim().length === 0}
            data-testid="record-procedure-form-test-btn"
          >
            {t('recordProcedures.form.testButton')}
          </Button>

          {(testFieldsError ?? testError) && (
            <p
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
              data-testid="record-procedure-form-test-error"
            >
              {testFieldsError ?? testError}
            </p>
          )}

          {testResult && testResult.kind === 'validation' && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${severityBannerClass[testResult.severity]}`}
              data-testid="record-procedure-form-test-result"
            >
              <p className="font-medium">
                {t(`recordProcedures.form.testSeverity.${testResult.severity}`)}
              </p>
              {testResult.message && <p className="mt-0.5">{testResult.message}</p>}
            </div>
          )}

          {testResult && testResult.kind === 'action' && (
            <div
              className="space-y-1 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
              data-testid="record-procedure-form-test-result"
            >
              <p>{t('recordProcedures.form.testRowCount', { count: testResult.rowCount })}</p>
              {testResult.message && (
                <p data-testid="record-procedure-form-test-message">
                  <span className="text-text-tertiary">
                    {t('recordProcedures.form.testMessageLabel')}
                  </span>{' '}
                  <span className="font-medium text-text-primary">{testResult.message}</span>
                </p>
              )}
              <p className="text-xs text-text-tertiary">
                {t('recordProcedures.form.testDryRunNote')}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
